import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuthStore } from "../../Store/auth";
import {
  FiArrowLeft,
  FiPhone,
  FiMail,
  FiMessageCircle,
  FiCheck,
  FiX,
  FiCalendar,
  FiClock,
  FiUser,
  FiHash,
} from "react-icons/fi";
import { API_ORIGIN } from "../../config/api";

const API = API_ORIGIN;

const ymd = (d) => new Date(d).toISOString().slice(0, 10);

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, coachId, logout } = useAuthStore();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false); // for action buttons

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Try direct booking
      const res = await axios.get(`${API}/api/Booking/${id}`, authHeaders);
      setBooking(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return logout();
      // Fallback: from coach list
      try {
        const list = await axios.get(`${API}/api/Booking/coach/${coachId}`, authHeaders);
        const found = (list.data || []).find((b) => String(b.id) === String(id));
        if (found) setBooking(found);
        else toast.error("Booking not found.");
      } catch (e2) {
        if (e2?.response?.status === 401) logout();
        toast.error("Failed to load booking.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, coachId, authHeaders, logout]);

  useEffect(() => { load(); }, [load]);

  const StatusBadge = ({ status }) => {
    const map = {
      0: "bg-yellow-100 text-yellow-800",
      1: "bg-green-100 text-green-800",
      2: "bg-red-100 text-red-800",
      3: "bg-gray-200 text-gray-700",
    };
    const text = { 0: "Pending", 1: "Accepted", 2: "Cancelled", 3: "Deleted" }[status] || "Pending";
    return <span className={`px-3 py-1 rounded-full text-sm font-semibold ${map[status]}`}>{text}</span>;
  };

  const canAccept = booking?.status === 0;
  const canCancel = booking && (booking.status === 0 || booking.status === 1);

  // Accept -> status=1, then remove availability
  const acceptBooking = async () => {
    if (!booking || busy || !canAccept) return;
    if (!window.confirm("Accept this booking and lock the slot?")) return;

    setBusy(true);
    try {
      await axios.put(`${API}/api/Booking/${id}/status`, { status: 1 }, authHeaders);

      // Remove matching availability
      const cleanTimeSlot = String(booking.timeSlot || "").trim();
      const dYMD = ymd(booking.bookingDate);
      const availableDate = `${dYMD}T00:00:00.000Z`;

      try {
        const res = await axios.get(
          `${API}/api/Availability/find?coachId=${coachId}&date=${encodeURIComponent(
            availableDate
          )}&timeSlot=${encodeURIComponent(cleanTimeSlot)}`,
          authHeaders
        );
        const availability = res.data;
        if (availability?.id) {
          await axios.delete(`${API}/api/Availability/${availability.id}`, authHeaders);
        }
      } catch {
        // it's okay if not found
      }

      setBooking((b) => ({ ...b, status: 1 }));
      toast.success("Booking accepted and availability removed.");
    } catch (err) {
      if (err?.response?.status === 401) logout();
      console.error(err);
      toast.error("Could not accept booking.");
    } finally {
      setBusy(false);
    }
  };

  // Cancel -> status=2. If previously accepted, recreate availability for that slot.
  const cancelBooking = async () => {
    if (!booking || busy || !canCancel) return;
    if (!window.confirm("Cancel this booking?")) return;

    const wasAccepted = booking.status === 1;

    setBusy(true);
    try {
      await axios.put(`${API}/api/Booking/${id}/status`, { status: 2 }, authHeaders);

      // If it was accepted, re-open the slot
      if (wasAccepted) {
        const dYMD = ymd(booking.bookingDate);
        const availableDateISO = `${dYMD}T00:00:00.000Z`;
        try {
          await axios.post(
            `${API}/api/Availability`,
            { coachId, availableDate: availableDateISO, timeSlot: booking.timeSlot },
            authHeaders
          );
        } catch {
          // ignore if duplicate or fails
        }
      }

      setBooking((b) => ({ ...b, status: 2 }));
      toast.warn("Booking cancelled.");
    } catch (err) {
      if (err?.response?.status === 401) logout();
      console.error(err);
      toast.error("Failed to cancel booking.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-10 bg-gray-200 rounded-lg w-40" />
          <div className="h-28 bg-gray-200 rounded-xl" />
          <div className="h-20 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <FiArrowLeft /> Back
        </button>
        <div className="border rounded-xl p-6 bg-white">Booking not found.</div>
      </div>
    );
  }

  const phoneDigits = String(booking.studentPhone || "").replace(/\D/g, "");

  return (
    <div className="relative">
      {/* Sticky mobile action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur border-t sm:hidden">
        <div className="max-w-3xl mx-auto p-3 flex gap-2">
          <button
            onClick={acceptBooking}
            disabled={!canAccept || busy}
            className={`flex-1 h-12 rounded-lg text-white font-semibold shadow ${
              canAccept && !busy ? "bg-green-600 hover:bg-green-700" : "bg-green-300 cursor-not-allowed"
            }`}
          >
            <div className="flex items-center justify-center gap-2"><FiCheck /> Accept</div>
          </button>
          <button
            onClick={cancelBooking}
            disabled={!canCancel || busy}
            className={`flex-1 h-12 rounded-lg text-white font-semibold shadow ${
              canCancel && !busy ? "bg-red-600 hover:bg-red-700" : "bg-red-300 cursor-not-allowed"
            }`}
          >
            <div className="flex items-center justify-center gap-2"><FiX /> Cancel</div>
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft /> Back
          </button>
          <StatusBadge status={booking.status} />
        </div>

        {/* Card */}
        <div className="border rounded-2xl shadow-sm bg-white overflow-hidden">
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h1 className="text-2xl font-extrabold flex items-center gap-2">
                <FiUser /> {booking.studentName || "—"}
              </h1>
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <FiHash /> ID: {booking.id}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <FiMail className="text-gray-500" />
                <span className="font-medium">Email:</span> {booking.studentEmail || "—"}
              </div>
              <div className="flex items-center gap-2">
                <FiPhone className="text-gray-500" />
                <span className="font-medium">Phone:</span> {booking.studentPhone || "—"}
              </div>
              <div className="flex items-center gap-2">
                <FiCalendar className="text-gray-500" />
                <span className="font-medium">Date:</span>{" "}
                {new Date(booking.bookingDate).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-2">
                <FiClock className="text-gray-500" />
                <span className="font-medium">Time slot:</span> {booking.timeSlot || "—"}
              </div>
            </div>

            {/* Contact shortcuts */}
            <div className="mt-4 flex flex-wrap gap-2">
              {booking.studentEmail && (
                <a
                  href={`mailto:${booking.studentEmail}`}
                  className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <FiMail /> Email
                </a>
              )}
              {booking.studentPhone && (
                <>
                  <a
                    href={`tel:${booking.studentPhone}`}
                    className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FiPhone /> Call
                  </a>
                  <a
                    href={`https://wa.me/${phoneDigits}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 rounded-lg border text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FiMessageCircle /> WhatsApp
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Desktop actions */}
          <div className="hidden sm:flex justify-end gap-2 p-5 border-t bg-gray-50">
            <button
              onClick={acceptBooking}
              disabled={!canAccept || busy}
              className={`px-4 py-2 rounded-lg text-white font-semibold shadow ${
                canAccept && !busy ? "bg-green-600 hover:bg-green-700" : "bg-green-300 cursor-not-allowed"
              }`}
            >
              <div className="flex items-center gap-2"><FiCheck /> Accept</div>
            </button>
            <button
              onClick={cancelBooking}
              disabled={!canCancel || busy}
              className={`px-4 py-2 rounded-lg text-white font-semibold shadow ${
                canCancel && !busy ? "bg-red-600 hover:bg-red-700" : "bg-red-300 cursor-not-allowed"
              }`}
            >
              <div className="flex items-center gap-2"><FiX /> Cancel</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
